import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useBrochure } from '../hooks/useBrochure';
import SEO from '../components/SEO';
import buildingImg from '../assets/building.png';

const Brochure = () => {
  const brochureUrl = useBrochure();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    interest: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { error } = await supabase.from('requests').insert([{
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        type: 'Brochure Download',
        message: `City: ${formData.city}\nInterest: ${formData.interest}\n\nMessage: ${formData.message}`
      }]);
      
      if (error) throw error;
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', city: '', interest: '', message: '' });
      setTimeout(() => setSuccess(false), 5000);
      
      if (brochureUrl) {
        window.open(brochureUrl, '_blank');
      } else {
        alert('Form submitted, but no brochure file is currently available for download.');
      }
    } catch (err) {
      console.error('Error submitting brochure request:', err);
      alert('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Download Corporate Brochure | HBB0011 REALTY"
        description="Download our comprehensive brochure to explore HBB0011 REALTY's exclusive projects, corporate architecture, and luxury offerings."
        keywords="real estate brochure, property catalogue, luxury real estate portfolio"
      />
      
      {/* 1. Hero Section (No Founder Image) */}
      <section className="relative w-full pt-32 pb-24 md:pt-40 md:pb-32 flex items-center overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 z-0">
          <img src={buildingImg} alt="Luxury Real Estate" className="w-full h-full object-cover opacity-20 mix-blend-luminosity" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/90 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6 uppercase">
            <Link to="/" className="text-white hover:text-[#D4AF37]">Home</Link>
            <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
            <span className="text-[#D4AF37]">Download Brochure</span>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight uppercase">
              Download our <br/><span className="text-[#D4AF37]">Brochure</span>
            </h1>
            <p className="text-xl text-gray-300 font-light mb-8 uppercase tracking-widest leading-relaxed border-l-2 border-[#D4AF37] pl-6">
              Discover our projects. Know our vision.<br/>See the difference.
            </p>
            <p className="text-sm sm:text-base text-gray-400 max-w-xl leading-relaxed">
              Get detailed insights into our iconic projects, world-class amenities, specifications, and investment benefits.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* 2. Download Form */}
          <div className="lg:col-span-7 bg-[#06121e] border border-gray-800 p-8 sm:p-12 rounded-sm shadow-xl">
            <h2 className="text-xl sm:text-2xl text-white font-bold mb-2 uppercase tracking-wide border-b border-gray-800 pb-4">Download Brochure</h2>
            <p className="text-gray-400 text-sm mb-10 mt-4">Fill in your details to download our company profile and project brochure.</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {success && (
                <div className="bg-green-500/20 border border-green-500/50 text-green-400 p-4 rounded-sm text-sm">
                  Your request has been sent! The brochure should start downloading shortly.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Full Name" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="Email Address" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="Phone Number" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
              </div>
              
              <select name="interest" value={formData.interest} onChange={handleInputChange} required className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-gray-400 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors">
                <option value="">I am interested in</option>
                <option value="Residential Projects">Residential Projects</option>
                <option value="Commercial Projects">Commercial Projects</option>
              </select>

              <textarea rows="3" name="message" value={formData.message} onChange={handleInputChange} placeholder="Message (Optional)" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"></textarea>

              <div className="flex items-start gap-3">
                <input type="checkbox" id="terms" required className="mt-1 accent-[#D4AF37]" />
                <label htmlFor="terms" className="text-gray-400 text-xs">I agree to the <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms & Conditions</Link>.</label>
              </div>

              <button type="submit" disabled={loading} className={`w-full font-bold text-xs uppercase tracking-widest py-4 rounded-sm transition-colors flex items-center justify-center gap-2 mt-4 ${loading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-[#D4AF37] text-[#041424] hover:bg-white'}`}>
                {loading ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-base">sync</span> Processing...
                  </>
                ) : (
                  <>
                    Download Brochure <span className="material-symbols-outlined text-base">download</span>
                  </>
                )}
              </button>
            </form>
            
            <div className="flex items-center justify-center gap-2 text-gray-500 text-[10px] mt-6">
              <span className="material-symbols-outlined text-[#D4AF37] text-sm">lock</span>
              Your information is secure and will not be shared.
            </div>
          </div>

          {/* 3. Brochure Contents Info */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <h3 className="text-xl text-white font-bold uppercase tracking-wide border-b border-gray-800 pb-4">What's Inside?</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
              {[
                { icon: 'domain', title: 'Company Overview', desc: 'Know more about our vision, mission and values.' },
                { icon: 'apartment', title: 'Our Projects', desc: 'Explore our ongoing and upcoming projects.' },
                { icon: 'grid_view', title: 'Amenities & Features', desc: 'Discover world-class amenities and lifestyle offerings.' },
                { icon: 'description', title: 'Specifications', desc: 'Detailed specifications and construction quality.' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 border border-gray-800 rounded-sm bg-[#06121e]">
                  <span className="material-symbols-outlined text-[#D4AF37] text-2xl">{item.icon}</span>
                  <div>
                    <h4 className="text-white text-sm font-bold uppercase tracking-wide mb-1">{item.title}</h4>
                    <p className="text-gray-400 text-[10px]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Download Badges */}
            <div className="flex flex-col sm:flex-row gap-6 pt-6 border-t border-gray-800">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl">bolt</span>
                <div>
                  <div className="text-white font-bold text-xs">Instant Access</div>
                  <div className="text-gray-500 text-[9px] uppercase tracking-widest">Download anywhere</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl">lock</span>
                <div>
                  <div className="text-white font-bold text-xs">100% Secure</div>
                  <div className="text-gray-500 text-[9px] uppercase tracking-widest">We respect privacy</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/919916710011" target="_blank" rel="noreferrer" className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center text-white text-3xl shadow-lg hover:bg-green-600 transition-colors z-50">
        <span className="material-symbols-outlined">chat</span>
      </a>

    </div>
  );
};

export default Brochure;